import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export function InvoiceRequestDialog({ lookupKey, title, amount, open, onOpenChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ org_name: user?.org_name || "", billing_address: "", po_number: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { onOpenChange(false); navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`); return; }
    setBusy(true);
    try {
      await api.post("/invoices/request", { lookup_key: lookupKey, ...form });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send request");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="invoice-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> Pay by invoice</DialogTitle>
          <DialogDescription>{title} — £{Number(amount).toLocaleString("en-GB")}. We'll email an invoice within one working day; 14-day payment terms by bank transfer. Access unlocks as soon as payment arrives.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-4 text-center space-y-3" data-testid="invoice-request-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <p className="font-semibold">Request sent</p>
            <p className="text-sm text-muted-foreground">Check your inbox for confirmation. You can track it under Billing.</p>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="invoice-request-close">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div><Label htmlFor="inv-org">Organisation / legal name</Label>
              <Input id="inv-org" required value={form.org_name} onChange={set("org_name")} data-testid="invoice-org-input" /></div>
            <div><Label htmlFor="inv-addr">Billing address</Label>
              <Textarea id="inv-addr" required rows={3} value={form.billing_address} onChange={set("billing_address")} data-testid="invoice-address-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="inv-po">PO number (optional)</Label>
                <Input id="inv-po" value={form.po_number} onChange={set("po_number")} data-testid="invoice-po-input" /></div>
              <div><Label htmlFor="inv-notes">Notes (optional)</Label>
                <Input id="inv-notes" value={form.notes} onChange={set("notes")} data-testid="invoice-notes-input" /></div>
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="invoice-request-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : user ? "Request invoice" : "Sign in to request an invoice"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PayByInvoiceLink({ lookupKey, title, amount, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`text-xs text-muted-foreground hover:text-primary underline-offset-2 hover:underline inline-flex items-center gap-1 ${className}`}
        data-testid={`pay-by-invoice-${lookupKey}`}>
        <FileText className="w-3 h-3" /> Pay by invoice / bank transfer
      </button>
      {open && <InvoiceRequestDialog lookupKey={lookupKey} title={title} amount={amount} open={open} onOpenChange={setOpen} />}
    </>
  );
}
